import { PATIENT_REFILL_ROUTE } from '@/routes/route-paths';
import { Logger } from '@/utils/logger';
import { usePrevious } from '@montugroup/circuit-shared';
import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import { useInterval } from '@montugroup/circuit-shared/hooks/useInterval';
import useMutationAsQuery from '@montugroup/circuit-shared/hooks/useMutationAsQuery';
import useUserProfileData from '@montugroup/circuit-shared/hooks/user/useGetUserProfile';
import type { Address } from '@montugroup/circuit-shared/types/address';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { managePersistedOrderDetails } from '../patient/refill/persistedOrderDetails';
import { userAddressToAddress } from './useClickAndCollectLocationQuery';
import { useDeliveryOptionsCacheKeyStore } from './useDeliveryOptionsCacheKeyStore';

const logger = new Logger('useDeliveryOptions');
const customerPortalClient = getTsCustomerPortalClient();
const INTERVAL_DURATION_MS = 3_000;
const DEFAULT_VALID_BEFORE_MINUTES = 15;

export default function useDeliveryOptions({ addressOverride }: { addressOverride?: Address } = {}) {
  const navigate = useNavigate();
  const persistedOrder = useMemo(() => managePersistedOrderDetails(), []);
  const products = useMemo(
    () => persistedOrder.getSelection()?.map((p) => ({ productId: p.id, quantity: p.quantity })) ?? [],
    [persistedOrder],
  );
  const { data: userProfileData, getUserState } = useUserProfileData();
  const userAddressAsAddress = useMemo(() => {
    if (!userProfileData?.user) {
      return null;
    }

    return userAddressToAddress(userProfileData.user, getUserState);
  }, [userProfileData?.user, getUserState]);

  const { previousCacheKey, cacheKey } = useDeliveryOptionsCacheKeyStore({
    products,
    address: addressOverride || userAddressAsAddress!,
  });
  const hasCacheKeyChanged = !!previousCacheKey && previousCacheKey !== cacheKey;
  const hasCacheKeySettled = previousCacheKey === cacheKey;

  const queryClient = customerPortalClient.useQueryClient();
  const refetchDeliveryOptions = useCallback(async () => {
    logger.info('deliveryOptionsRefresh: called refetchDeliveryOptions');
    queryClient.invalidateQueries({ queryKey: customerPortalClient.listCartDeliveryOptionsAndReserveInventory.keys });
  }, [queryClient]);

  useLayoutEffect(() => {
    if (products.length === 0) {
      navigate(PATIENT_REFILL_ROUTE, { replace: true });
      return;
    }
  }, [navigate, products.length]);

  // NOTE: We are manually invalidating the deliveryOption base queryKey when the more specific cacheKey changes,
  // this is so we can ensure only the latest request to this endpoint survives in the cache
  useLayoutEffect(() => {
    if (hasCacheKeyChanged) {
      logger.info('deliveryOptionsRefresh: refreshing due to cacheKey change', {
        previousCacheKey,
        cacheKey,
      });
      refetchDeliveryOptions();
    }
  }, [cacheKey, hasCacheKeyChanged, previousCacheKey, refetchDeliveryOptions]);

  const { data, isFetching, isError, dataUpdatedAt } = useMutationAsQuery(
    customerPortalClient.listCartDeliveryOptionsAndReserveInventory,
    {
      body: {
        data: {
          products,
          ...(addressOverride?.geometry?.location
            ? {
                customAllocationLocation: {
                  coordinates: {
                    latitude: addressOverride.geometry.location.lat,
                    longitude: addressOverride.geometry.location.lng,
                  },
                },
              }
            : {}),
        },
      },
    },
    {
      enabled: !!products.length,
      staleTime: 1000 * 60 * DEFAULT_VALID_BEFORE_MINUTES,
      gcTime: 1000 * 60 * DEFAULT_VALID_BEFORE_MINUTES,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      placeholderData: undefined,
    },
    customerPortalClient.listCartDeliveryOptionsAndReserveInventory.keys,
    queryClient,
  );

  const deliveryOptions = useMemo(() => {
    if (!data) {
      return { SameDayAfternoon: [], SameDay: [], Standard: [], ClickAndCollect: [] };
    }

    const { delivery, ClickAndCollect } = data.body.data;
    return {
      SameDayAfternoon: delivery?.SameDayAfternoon ?? [],
      SameDay: delivery?.SameDay ?? [],
      Standard: delivery?.Standard ?? [],
      ClickAndCollect: ClickAndCollect ?? [],
    };
  }, [data]);

  const nextRefreshAt: {
    reason: 'fallback' | 'validBefore' | 'cutoffTime';
    deliveryOption: null | (typeof deliveryOptions)[keyof typeof deliveryOptions][0];
    time: DateTime;
  } = useMemo(() => {
    // Default to refreshing in 15 minutes as a fallback if something goes wrong - we will monitor if this is ever hit
    const fallbackTime = {
      reason: 'fallback',
      deliveryOption: null,
      // dataUpdatedAt on first load is 0
      time: DateTime.fromMillis(dataUpdatedAt ? dataUpdatedAt : DateTime.now().toMillis()).plus({
        minutes: DEFAULT_VALID_BEFORE_MINUTES,
      }),
    } as const;

    let smallestTime: {
      reason: 'fallback' | 'validBefore' | 'cutoffTime';
      deliveryOption: null | (typeof deliveryOptions)[keyof typeof deliveryOptions][0];
      time: DateTime;
    } = fallbackTime;

    const deliveryOptionKeys = Object.keys(deliveryOptions) as (keyof typeof deliveryOptions)[];

    // NOTE: This shouldUseFallback logic is only needed to support uses who have manually overriden their local time, otherwise their options will refresh continiously (blocking their checkout)
    const shouldUseFallback = deliveryOptionKeys.some((optionKey: keyof typeof deliveryOptions) => {
      const deliveryOption = deliveryOptions[optionKey][0];

      if (deliveryOption) {
        const validBeforeMilliseconds = DateTime.fromISO(deliveryOption.validBefore).toMillis();

        if (validBeforeMilliseconds < dataUpdatedAt) {
          logger.error(
            `deliveryOptionsRefresh: user's local time is more than ${DEFAULT_VALID_BEFORE_MINUTES} minutes in the future, using fallback nextRefreshAt`,
            {
              fallbackTime,
              validBeforeMilliseconds,
              validBeforeISO: deliveryOption.validBefore,
              dataUpdatedAt,
              dataUpdatedAtISO: DateTime.fromMillis(dataUpdatedAt).toISO(),
              nowMillis: DateTime.now().toMillis(),
              nowISO: DateTime.now().toISO(),
              deliveryOptions,
            },
          );

          return true;
        }
      }
    });

    if (shouldUseFallback) {
      return fallbackTime;
    }

    deliveryOptionKeys.forEach((optionKey: keyof typeof deliveryOptions) => {
      const deliveryOption = deliveryOptions[optionKey][0];
      if (deliveryOption) {
        if (
          deliveryOption.validBefore &&
          DateTime.fromISO(deliveryOption.validBefore).isValid &&
          DateTime.fromISO(deliveryOption.validBefore) < smallestTime.time
        ) {
          const newSmallestTime = {
            reason: 'validBefore',
            deliveryOption: deliveryOption,
            time: DateTime.fromISO(deliveryOption.validBefore) as DateTime,
          } as const;

          smallestTime = newSmallestTime;
        }

        if (
          deliveryOption &&
          'cutoffTime' in deliveryOption &&
          typeof deliveryOption.cutoffTime === 'string' &&
          DateTime.fromISO(deliveryOption.cutoffTime).isValid &&
          DateTime.fromISO(deliveryOption.cutoffTime) < smallestTime.time
        ) {
          const newSmallestTime = {
            reason: 'cutoffTime',
            deliveryOption: deliveryOption,
            time: DateTime.fromISO(deliveryOption.cutoffTime) as DateTime,
          } as const;

          smallestTime = newSmallestTime;
        }
      }
    });

    return smallestTime;
  }, [dataUpdatedAt, deliveryOptions]);

  // NOTE: previousNextRefreshAt, previousDataUpdatedAt & this useEffect are just for logging the setup of a nextRefreshAt
  const previousNextRefreshAt = usePrevious(nextRefreshAt);
  const previousDataUpdatedAt = usePrevious(dataUpdatedAt);
  useEffect(() => {
    if (!isFetching && previousNextRefreshAt && !nextRefreshAt.time.equals(previousNextRefreshAt.time)) {
      logger.info(`deliveryOptionsRefresh: got ${previousDataUpdatedAt ? 'new' : 'first'} nextRefreshAt`, {
        previousDataUpdatedAt,
        dataUpdatedAt,
        dataUpdatedAtISO: DateTime.fromMillis(dataUpdatedAt).toISO(),
        nextRefreshAt,
        deliveryOptions,
        nowISO: DateTime.now().toISO(),
        nextRefreshAtTimeISO: nextRefreshAt.time.toISO(),
        nextRefreshAtMinutes: nextRefreshAt.time.diffNow('minutes').minutes,
        nextRefreshAtInMilliseconds: nextRefreshAt.time.diffNow('milliseconds').milliseconds,
      });
    }
  }, [dataUpdatedAt, deliveryOptions, isFetching, nextRefreshAt, previousDataUpdatedAt, previousNextRefreshAt]);

  useInterval(() => {
    if (!isFetching && DateTime.now().toMillis() > nextRefreshAt.time.toMillis() - INTERVAL_DURATION_MS) {
      logger.info('deliveryOptionsRefresh: refreshing via useInterval', {
        dataUpdatedAt,
        dataUpdatedAtISO: DateTime.fromMillis(dataUpdatedAt).toISO(),
        nextRefreshAt,
        deliveryOptions,
        nowISO: DateTime.now().toISO(),
        nextRefreshAtTimeISO: nextRefreshAt.time.toISO(),
        nextRefreshAtMinutes: nextRefreshAt.time.diffNow('minutes').minutes,
        nextRefreshAtInMilliseconds: nextRefreshAt.time.diffNow('milliseconds').milliseconds,
      });
      refetchDeliveryOptions();
    }
  }, INTERVAL_DURATION_MS);

  useEffect(() => {
    if (
      hasCacheKeySettled &&
      !isFetching &&
      DateTime.now().toMillis() > nextRefreshAt.time.toMillis() - INTERVAL_DURATION_MS
    ) {
      logger.info('deliveryOptionsRefresh: refreshing due to time past nextRefreshAt during render', {
        previousCacheKey,
        cacheKey,
        dataUpdatedAt,
        dataUpdatedAtISO: DateTime.fromMillis(dataUpdatedAt).toISO(),
        nextRefreshAt,
        deliveryOptions,
        nowISO: DateTime.now().toISO(),
        nextRefreshAtTimeISO: nextRefreshAt.time.toISO(),
        nextRefreshAtMinutes: nextRefreshAt.time.diffNow('minutes').minutes,
        nextRefreshAtInMilliseconds: nextRefreshAt.time.diffNow('milliseconds').milliseconds,
      });
      refetchDeliveryOptions();
    }
  }, [
    cacheKey,
    dataUpdatedAt,
    deliveryOptions,
    hasCacheKeySettled,
    isFetching,
    nextRefreshAt,
    previousCacheKey,
    refetchDeliveryOptions,
  ]);

  return { deliveryOptions, refetchDeliveryOptions, isFetching, isError };
}
